<template>
  <div>
    <!-- Hero Header Section -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-school-outline</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">ข้อมูลวุฒิการศึกษา</h1>
          <p class="hero-subtitle">จัดการประวัติการศึกษาและคุณวุฒิทางวิชาการ</p>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          class="add-btn"
          large
          depressed
          @click="personnel_educationAdd()"
        >
          <v-icon left>mdi-plus</v-icon>
          เพิ่มวุฒิการศึกษา
        </v-btn>
      </div>
    </v-card>

    <!-- Statistics Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-certificate</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ personnel_educations.length }}</span>
            <span class="stat-label">วุฒิทั้งหมด</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-2" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-school</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByLevel("ปริญญาตรี") }}</span>
            <span class="stat-label">ปริญญาตรี</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-3" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-trophy-award</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByLevel("ปริญญาโท") }}</span>
            <span class="stat-label">ปริญญาโท</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-4" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-star-circle</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByLevel("ปริญญาเอก") }}</span>
            <span class="stat-label">ปริญญาเอก</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Data Table Card -->
    <v-card class="main-table-card" elevation="0">
      <v-card-title class="table-header">
        <div class="header-left">
          <v-icon class="mr-3" color="#667eea">mdi-format-list-bulleted</v-icon>
          <span class="header-title">รายการวุฒิการศึกษา</span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="ค้นหาวุฒิการศึกษา..."
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          style="max-width: 350px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="personnel_educations"
        :search="search"
        :items-per-page="10"
        class="modern-table"
        loading-text="กำลังโหลดข้อมูล..."
        no-data-text="ไม่พบข้อมูลวุฒิการศึกษา"
        no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
      >
        <template v-slot:item="{ item, index }">
          <tr class="table-row">
            <td class="text-center">
              <v-avatar
                size="32"
                color="#667eea"
                class="white--text font-weight-bold"
              >
                {{ index + 1 }}
              </v-avatar>
            </td>
            <td>
              <v-chip
                small
                color="#e8eaf6"
                text-color="#3949ab"
                class="font-weight-medium"
              >
                {{ item.id_branch }}
              </v-chip>
            </td>
            <td>
              <div class="branch-cell">
                <v-icon small color="#667eea" class="mr-2"
                  >mdi-tag-outline</v-icon
                >
                {{ item.name_branch }}
              </div>
            </td>
            <td>
              <span class="sub-branch-text">{{ item.name_sub_branch }}</span>
            </td>
            <td class="text-center">
              <v-chip
                small
                :color="getEducationLevelColor(item.education_level)"
                dark
                class="education-chip"
              >
                <v-icon left small>{{
                  getEducationLevelIcon(item.education_level)
                }}</v-icon>
                {{ item.education_level }}
              </v-chip>
            </td>
            <td>
              <span class="faculty-text">{{ item.faculty_name }}</span>
            </td>
            <td>
              <div class="branch-name-cell">
                <v-icon small color="#43a047" class="mr-1"
                  >mdi-book-education-outline</v-icon
                >
                {{ item.branch_name }}
              </div>
            </td>
            <td>
              <div class="academy-cell">
                <v-icon small color="#fb8c00" class="mr-1">mdi-domain</v-icon>
                {{ item.academy_name }}
              </div>
            </td>
            <td class="text-center">
              <v-chip small outlined color="#5c6bc0">
                <v-icon left small>mdi-calendar</v-icon>
                {{ item.year_finish }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip
                small
                :color="getGradeColor(item.academic_results)"
                dark
                class="grade-chip"
              >
                {{ item.academic_results }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn edit-btn mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_educationEdit(item.id_red)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>แก้ไข</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn delete-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_educationDelete(item.id_red)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>ลบ</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Branch Reference Card -->
    <v-card class="branch-reference-card mt-6" elevation="0">
      <v-card-title class="branch-header">
        <div class="d-flex align-center">
          <div class="branch-icon-wrapper mr-3">
            <v-icon color="white" size="24">mdi-database-search</v-icon>
          </div>
          <div>
            <div class="branch-title">ตรวจสอบสาขาวิชาเอก</div>
            <div class="branch-subtitle">ข้อมูลสาขาวิชาในระบบ สอศ.</div>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search_branch"
          append-icon="mdi-magnify"
          label="ค้นหาสาขาวิชา..."
          single-line
          hide-details
          dense
          outlined
          class="search-field-branch"
          style="max-width: 300px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :headers="header_branchs"
        :items="showbranchdata"
        :search="search_branch"
        :items-per-page="5"
        class="branch-table"
        no-data-text="ไม่พบข้อมูลสาขาวิชา"
      >
        <template v-slot:item="{ item }">
          <tr class="branch-row">
            <td class="text-center">
              <v-chip small color="#fff3e0" text-color="#e65100">
                {{ item.id_branch }}
              </v-chip>
            </td>
            <td>
              <div class="d-flex align-center">
                <v-icon small color="#ff9800" class="mr-2"
                  >mdi-bookmark-outline</v-icon
                >
                <span class="font-weight-medium">{{ item.name_branch }}</span>
              </div>
            </td>
            <td>
              <span class="detail-text">{{ item.detail_branch }}</span>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Education Dialog -->
    <v-dialog
      v-model="addpersonnel_educationdialog"
      persistent
      max-width="900px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header add-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-plus-circle-outline</v-icon
          >
          <span>เพิ่มวุฒิการศึกษาใหม่</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addpersonnel_educationdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="addpersonnel_educationform" lazy-validation>
            <!-- Section 1: การจัดกลุ่มสาขา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#667eea" class="mr-2">mdi-folder-outline</v-icon>
                การจัดกลุ่มสาขาวิชา
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="addpersonnel_education.education_level"
                    :items="education_level"
                    item-value="text"
                    item-text="text"
                    label="ระดับการศึกษา"
                    @change="cls_degreeQuery_code()"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกระดับการศึกษา']"
                    prepend-inner-icon="mdi-school"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    :items="branch_s"
                    item-text="name_branch"
                    item-value="id_branch"
                    label="ประเภทสาขาวิชา (สอศ.)"
                    v-model="addpersonnel_education.id_branch"
                    :rules="[v => !!v || 'กรุณาเลือกประเภทสาขา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag-outline"
                    @change="branch_sub_dQueryAll()"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    :items="branch_sub_ds"
                    item-text="name_sub_branch"
                    item-value="id_branch_sub"
                    label="กลุ่มวุฒิการศึกษา"
                    v-model="addpersonnel_education.ed_id_branch_sub"
                    :rules="[v => !!v || 'กรุณาเลือกกลุ่มวุฒิ']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-layers-outline"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </div>

            <!-- Section 2: ข้อมูลวุฒิการศึกษา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#43a047" class="mr-2"
                  >mdi-certificate-outline</v-icon
                >
                ข้อมูลวุฒิการศึกษา
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    :items="cls_degree_degree"
                    item-text="cls_degree_name"
                    item-value="cls_degree_name"
                    v-model="addpersonnel_education.faculty_name"
                    label="วุฒิการศึกษา (เช่น วท.บ., บธ.บ.)"
                    :rules="[v => !!v || 'กรุณาเลือกวุฒิการศึกษา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-file-certificate-outline"
                    class="modern-input"
                  >
                    <template v-slot:item="{ item }">
                      <div class="degree-item">
                        <div class="degree-name">
                          {{ item.cls_degree_name }}
                        </div>
                        <div class="degree-level">{{ item.level_name }}</div>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_education.branch_name"
                    label="สาขาวิชา"
                    placeholder="เช่น ระบบสารสนเทศทางคอมพิวเตอร์"
                    :rules="[v => !!v || 'กรุณาระบุสาขาวิชา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-book-education-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_education.academy_name"
                    label="สถานศึกษาที่จบ"
                    placeholder="เช่น มหาวิทยาลัยเชียงใหม่"
                    :rules="[v => !!v || 'กรุณาระบุสถานศึกษา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-domain"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section 3: ผลการศึกษา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#fb8c00" class="mr-2">mdi-chart-line</v-icon>
                ผลการศึกษา
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_education.year_finish"
                    label="ปีการศึกษาที่สำเร็จ (พ.ศ.)"
                    placeholder="เช่น 2565"
                    type="number"
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_education.academic_results"
                    label="เกรดเฉลี่ย (GPA)"
                    placeholder="เช่น 3.50"
                    type="number"
                    step="0.01"
                    min="0"
                    max="4"
                    outlined
                    dense
                    prepend-inner-icon="mdi-star-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            large
            @click="addpersonnel_educationdialog = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#667eea"
            dark
            @click="addpersonnel_educationSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Education Dialog -->
    <v-dialog
      v-model="editpersonnel_educationdialog"
      persistent
      max-width="900px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header edit-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-pencil-outline</v-icon
          >
          <span>แก้ไขวุฒิการศึกษา</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editpersonnel_educationdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="editpersonnel_educationform" lazy-validation>
            <!-- Section 1: การจัดกลุ่มสาขา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#fb8c00" class="mr-2">mdi-folder-outline</v-icon>
                การจัดกลุ่มสาขาวิชา
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="editpersonnel_education.education_level"
                    :items="education_level"
                    item-value="text"
                    item-text="text"
                    label="ระดับการศึกษา"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกระดับการศึกษา']"
                    prepend-inner-icon="mdi-school"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    :items="branch_s"
                    item-text="name_branch"
                    item-value="id_branch"
                    label="ประเภทสาขาวิชา (สอศ.)"
                    v-model="editpersonnel_education.id_branch"
                    :rules="[v => !!v || 'กรุณาเลือกประเภทสาขา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag-outline"
                    @change="branch_sub_d_editQueryAll()"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    :items="branch_sub_ds"
                    item-text="name_sub_branch"
                    item-value="id_branch_sub"
                    label="กลุ่มวุฒิการศึกษา"
                    v-model="editpersonnel_education.ed_id_branch_sub"
                    :rules="[v => !!v || 'กรุณาเลือกกลุ่มวุฒิ']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-layers-outline"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </div>

            <!-- Section 2: ข้อมูลวุฒิการศึกษา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#43a047" class="mr-2"
                  >mdi-certificate-outline</v-icon
                >
                ข้อมูลวุฒิการศึกษา
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_education.faculty_name"
                    label="วุฒิการศึกษา (เช่น วท.บ., บธ.บ.)"
                    :rules="[v => !!v || 'กรุณาระบุวุฒิการศึกษา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-file-certificate-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_education.branch_name"
                    label="สาขาวิชา"
                    :rules="[v => !!v || 'กรุณาระบุสาขาวิชา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-book-education-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_education.academy_name"
                    label="สถานศึกษาที่จบ"
                    :rules="[v => !!v || 'กรุณาระบุสถานศึกษา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-domain"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section 3: ผลการศึกษา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#e53935" class="mr-2">mdi-chart-line</v-icon>
                ผลการศึกษา
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_education.year_finish"
                    label="ปีการศึกษาที่สำเร็จ (พ.ศ.)"
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_education.academic_results"
                    label="เกรดเฉลี่ย (GPA)"
                    outlined
                    dense
                    prepend-inner-icon="mdi-star-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            large
            @click="editpersonnel_educationdialog = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#fb8c00"
            dark
            @click="editpersonnel_educationSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกการแก้ไข
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deletepersonnel_educationdialog"
      persistent
      max-width="450px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card delete-dialog">
        <v-card-title class="dialog-header delete-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-delete-alert-outline</v-icon
          >
          <span>ยืนยันการลบข้อมูล</span>
        </v-card-title>

        <v-card-text class="delete-content text-center">
          <v-form ref="deletepersonnel_educationform" lazy-validation>
            <div class="delete-icon-wrapper">
              <v-icon size="64" color="#e53935"
                >mdi-alert-circle-outline</v-icon
              >
            </div>
            <div class="delete-message">
              คุณต้องการลบวุฒิการศึกษานี้หรือไม่?
            </div>
            <v-chip
              class="delete-item-chip mt-3"
              color="#ffebee"
              text-color="#c62828"
              large
            >
              <v-icon left color="#c62828">mdi-school</v-icon>
              {{ editpersonnel_education.education_level }} -
              {{ editpersonnel_education.branch_name }}
            </v-chip>
            <div class="delete-warning mt-4">
              <v-icon small color="#ff9800" class="mr-1">mdi-alert</v-icon>
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            large
            @click="deletepersonnel_educationdialog = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#e53935"
            dark
            @click="deletepersonnel_educationubmit()"
            class="delete-confirm-btn"
          >
            <v-icon left>mdi-delete-outline</v-icon>
            ลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
      class="modern-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "PersonnelEducation",
  props: {
    editpersonnel_temporary: {
      type: [Object, Array],
      required: false,
      default: () => ({})
    }
  },

  data() {
    return {
      search_branch: "",
      ApiKey: "HRvec2021",
      loading: true,
      search: "",
      addpersonnel_educationdialog: false,
      editpersonnel_educationdialog: false,
      deletepersonnel_educationdialog: false,
      snackbar: {
        show: false,
        color: "",
        timeout: 4000,
        icon: "",
        text: ""
      },
      headers: [
        {
          text: "#",
          align: "center",
          value: "number_row",
          width: "60px",
          sortable: false
        },
        { text: "รหัส", align: "center", value: "id_branch", width: "80px" },
        { text: "ประเภทสาขา สอศ.", align: "left", value: "name_branch" },
        { text: "วุฒิตรงระบบ", align: "left", value: "name_sub_branch" },
        {
          text: "ระดับ",
          align: "center",
          value: "education_level",
          width: "140px"
        },
        {
          text: "วุฒิการศึกษา",
          align: "center",
          value: "faculty_name",
          width: "100px"
        },
        { text: "สาขาวิชา", align: "left", value: "branch_name" },
        { text: "สถานศึกษา", align: "left", value: "academy_name" },
        {
          text: "ปีที่จบ",
          align: "center",
          value: "year_finish",
          width: "100px"
        },
        {
          text: "GPA",
          align: "center",
          value: "academic_results",
          width: "80px"
        },
        {
          text: "จัดการ",
          align: "center",
          value: "actions",
          sortable: false,
          width: "100px"
        }
      ],
      header_branchs: [
        { text: "รหัส", align: "center", value: "id_branch", width: "100px" },
        { text: "สาขาวิชา", align: "left", value: "name_branch" },
        { text: "รายละเอียด", align: "left", value: "detail_branch" }
      ],
      personnel_educations: [],
      addpersonnel_education: {},
      editpersonnel_education: {},
      branch_s: [],
      showbranchdata: [],
      branch_sub_ds: [],
      education_level: [
        { text: "ต่ำกว่า ป.ตรี", value: "L" },
        { text: "ปริญญาตรี", value: "B" },
        { text: "ปริญญาโท", value: "M" },
        { text: "ปริญญาเอก", value: "D" }
      ],
      cls_degree: [],
      cls_degree_degree: []
    };
  },

  async mounted() {
    await this.personnel_educationsQueryAll();
    await this.getAllbranchdata();
    await this.cls_degreeQueryAll();
    await this.branchQueryAll();
  },

  methods: {
    // Helper methods
    countByLevel(level) {
      return this.personnel_educations.filter(
        item => item.education_level === level
      ).length;
    },

    getEducationLevelColor(level) {
      const colors = {
        "ต่ำกว่า ป.ตรี": "#78909c",
        ปริญญาตรี: "#5c6bc0",
        ปริญญาโท: "#7e57c2",
        ปริญญาเอก: "#ec407a"
      };
      return colors[level] || "#757575";
    },

    getEducationLevelIcon(level) {
      const icons = {
        "ต่ำกว่า ป.ตรี": "mdi-school-outline",
        ปริญญาตรี: "mdi-school",
        ปริญญาโท: "mdi-trophy-award",
        ปริญญาเอก: "mdi-star-circle"
      };
      return icons[level] || "mdi-school";
    },

    getGradeColor(grade) {
      const g = parseFloat(grade);
      if (g >= 3.5) return "#43a047";
      if (g >= 3.0) return "#7cb342";
      if (g >= 2.5) return "#fdd835";
      if (g >= 2.0) return "#fb8c00";
      return "#e53935";
    },

    showSnackbar(type, text) {
      const configs = {
        success: { color: "#43a047", icon: "mdi-check-circle-outline" },
        error: { color: "#e53935", icon: "mdi-alert-circle-outline" },
        warning: { color: "#fb8c00", icon: "mdi-alert-outline" }
      };
      this.snackbar = {
        show: true,
        timeout: 4000,
        text,
        ...configs[type]
      };
    },

    // API methods
    async branchQueryAll() {
      let result = await this.$http.post("branch.php", {
        ApiKey: this.ApiKey
      });
      this.branch_s = result.data;
    },

    async cls_degreeQueryAll() {
      let result = await this.$http.post("cls_degree.php", {
        ApiKey: this.ApiKey
      });
      this.cls_degree = result.data;
    },

    async cls_degreeQuery_code() {
      let result = await this.$http.post("cls_degree.php", {
        ApiKey: this.ApiKey,
        level_name: this.addpersonnel_education.education_level
      });
      this.cls_degree_degree = result.data;
    },

    async branch_sub_dQueryAll() {
      let result = await this.$http.post("branch_sub_d.php", {
        ApiKey: this.ApiKey,
        id_main_branch: this.addpersonnel_education.id_branch
      });
      this.branch_sub_ds = result.data;
    },

    async branch_sub_d_editQueryAll() {
      let result = await this.$http.post("branch_sub_d.php", {
        ApiKey: this.ApiKey,
        id_main_branch: this.editpersonnel_education.id_branch
      });
      this.branch_sub_ds = result.data;
    },

    async personnel_educationsQueryAll() {
      this.loading = true;
      let result = await this.$http
        .post("personnel_education.php", {
          ApiKey: this.ApiKey,
          id_card: this.editpersonnel_temporary.id_card
        })
        .finally(() => (this.loading = false));
      this.personnel_educations = result.data;
    },

    // Add operations
    personnel_educationAdd() {
      this.addpersonnel_education = {};
      this.addpersonnel_educationdialog = true;
    },

    async addpersonnel_educationSubmit() {
      if (this.$refs.addpersonnel_educationform.validate()) {
        this.addpersonnel_education.ApiKey = this.ApiKey;
        this.addpersonnel_education.id_card = this.editpersonnel_temporary.id_card;

        let result = await this.$http.post(
          "personnel_education.insert.php",
          this.addpersonnel_education
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "บันทึกสำเร็จ",
            text: "เพิ่มวุฒิการศึกษาเรียบร้อยแล้ว",
            showConfirmButton: false,
            timer: 1500
          });
          this.personnel_educationsQueryAll();
        } else {
          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
            showConfirmButton: true
          });
        }
        this.addpersonnel_educationdialog = false;
      }
    },

    // Edit operations
    async personnel_educationEdit(id_red) {
      let result = await this.$http.post("personnel_education.php", {
        ApiKey: this.ApiKey,
        id_red: id_red
      });
      this.editpersonnel_education = result.data;

      // Load branch sub data for edit
      if (this.editpersonnel_education.id_branch) {
        await this.branch_sub_d_editQueryAll();
      }

      this.editpersonnel_educationdialog = true;
    },

    async editpersonnel_educationSubmit() {
      if (this.$refs.editpersonnel_educationform.validate()) {
        this.editpersonnel_education.ApiKey = this.ApiKey;

        let result = await this.$http.post(
          "personnel_education.update.php",
          this.editpersonnel_education
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "แก้ไขสำเร็จ",
            text: "อัปเดตวุฒิการศึกษาเรียบร้อยแล้ว",
            showConfirmButton: false,
            timer: 1500
          });
          this.personnel_educationsQueryAll();
        } else {
          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: "ไม่สามารถแก้ไขข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
            showConfirmButton: true
          });
        }
        this.editpersonnel_educationdialog = false;
      }
    },

    // Delete operations
    async personnel_educationDelete(id_red) {
      let result = await this.$http.post("personnel_education.php", {
        ApiKey: this.ApiKey,
        id_red: id_red
      });
      this.editpersonnel_education = result.data;
      this.deletepersonnel_educationdialog = true;
    },

    async deletepersonnel_educationubmit() {
      this.editpersonnel_education.ApiKey = this.ApiKey;

      let result = await this.$http.post(
        "personnel_education.delete.php",
        this.editpersonnel_education
      );

      if (result.data.status === true) {
        Swal.fire({
          icon: "success",
          title: "ลบสำเร็จ",
          text: "ลบวุฒิการศึกษาเรียบร้อยแล้ว",
          showConfirmButton: false,
          timer: 1500
        });
        this.personnel_educationsQueryAll();
      } else {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
          showConfirmButton: true
        });
      }
      this.deletepersonnel_educationdialog = false;
    },

    async getAllbranchdata() {
      let result = await this.$http.post("branch.php", {
        ApiKey: this.ApiKey
      });
      this.showbranchdata = result.data;
    }
  }
};
</script>

<style scoped>
/* Import Google Font */
@import url("https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap");

.personnel-education-container {
  font-family: "Prompt", sans-serif;
  padding: 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  min-height: 100vh;
}

/* Hero Header */
.hero-header {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

.hero-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
}

.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 24px;
  backdrop-filter: blur(10px);
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.add-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #e53935 !important;
  font-weight: 500;
  border-radius: 12px;
  padding: 0 24px !important;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Statistics Cards */
.stats-row {
  margin-top: -20px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(230, 57, 53, 0.1);
  box-shadow: 0 2px 8px rgba(229, 57, 53, 0.05);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(229, 57, 53, 0.15);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-card-1 .stat-icon {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
}
.stat-card-2 .stat-icon {
  background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
}
.stat-card-3 .stat-icon {
  background: linear-gradient(135deg, #c62828 0%, #a41c3a 100%);
}
.stat-card-4 .stat-icon {
  background: linear-gradient(135deg, #bf360c 0%, #8d2815 100%);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #718096;
  margin-top: 4px;
  font-weight: 400;
}

/* Main Table Card */
.main-table-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(229, 57, 53, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.table-header {
  padding: 20px 24px;
  background: #fff5f5;
  border-bottom: 1px solid rgba(229, 57, 53, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.search-field {
  background: white;
}

.search-field >>> .v-input__slot {
  border-radius: 10px !important;
  border: 1px solid #e0e0e0 !important;
}

/* Data Table Styles */
.modern-table {
  font-family: "Prompt", sans-serif;
}

.modern-table >>> thead th {
  background: #fff5f5 !important;
  color: #c62828 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #ffcdd2 !important;
  padding: 16px 12px !important;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: #fff9f9 !important;
}

.table-row td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #ffe0e0 !important;
  font-size: 14px;
  color: #4a5568;
}

.branch-cell,
.branch-name-cell,
.academy-cell {
  display: flex;
  align-items: center;
}

.sub-branch-text {
  color: #718096;
  font-size: 13px;
}

.faculty-text {
  font-weight: 500;
  color: #e53935;
}

.education-chip {
  font-weight: 500;
  letter-spacing: 0;
}

.grade-chip {
  font-weight: 600;
  min-width: 50px;
  justify-content: center;
}

/* Action Buttons */
.action-btn {
  width: 32px !important;
  height: 32px !important;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #fff3e0 !important;
  color: #ff8f00 !important;
}

.edit-btn:hover {
  background: #ffecb3 !important;
  transform: scale(1.1);
}

.delete-btn {
  background: #ffebee !important;
  color: #e53935 !important;
}

.delete-btn:hover {
  background: #ffcdd2 !important;
  transform: scale(1.1);
}

/* Branch Reference Card */
.branch-reference-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(229, 57, 53, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.branch-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
  color: white;
}

.branch-icon-wrapper {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.branch-title {
  font-size: 16px;
  font-weight: 600;
}

.branch-subtitle {
  font-size: 12px;
  opacity: 0.9;
}

.search-field-branch {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
}

.branch-table >>> thead th {
  background: #fff5f5 !important;
  color: #c62828 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
}

.branch-row td {
  padding: 14px 16px !important;
}

.detail-text {
  color: #718096;
  font-size: 13px;
}

/* Dialog Styles */
.modern-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-header {
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.add-header {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
}

.edit-header {
  background: linear-gradient(135deg, #ff8f00 0%, #ff6f00 100%);
}

.delete-header {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
}

.dialog-content {
  padding: 24px !important;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  background: #fff5f5;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #ffcdd2;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title v-icon {
  color: #e53935;
}

.modern-input >>> .v-input__slot {
  background: white !important;
  border-radius: 10px !important;
  border: 1px solid #e0e0e0 !important;
}

.degree-item {
  padding: 8px 0;
}

.degree-name {
  font-weight: 500;
  color: #2d3748;
}

.degree-level {
  font-size: 12px;
  color: #718096;
}

.dialog-actions {
  padding: 16px 24px;
  background: #fff5f5;
}

.cancel-btn {
  color: #718096 !important;
  font-weight: 500;
  text-transform: none;
}

.save-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  padding: 0 24px !important;
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%) !important;
}

/* Delete Dialog */
.delete-content {
  padding: 32px 24px !important;
}

.delete-icon-wrapper {
  margin-bottom: 20px;
}

.delete-message {
  font-size: 18px;
  font-weight: 500;
  color: #2d3748;
}

.delete-item-chip {
  font-weight: 500;
}

.delete-warning {
  font-size: 13px;
  color: #e53935;
}

.delete-confirm-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  background: #e53935 !important;
}

/* Snackbar */
.modern-snackbar >>> .v-snack__wrapper {
  border-radius: 12px;
  font-family: "Prompt", sans-serif;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-content {
    flex-wrap: wrap;
    gap: 16px;
  }

  .hero-icon-wrapper {
    width: 60px;
    height: 60px;
    margin-right: 16px;
  }

  .hero-title {
    font-size: 22px;
  }

  .add-btn {
    width: 100%;
    margin-top: 16px;
  }
}

@media (max-width: 600px) {
  .personnel-education-container {
    padding: 16px;
  }

  .hero-content {
    padding: 20px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 24px;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.5s ease forwards;
}

.stat-card:nth-child(1) {
  animation-delay: 0.1s;
}
.stat-card:nth-child(2) {
  animation-delay: 0.2s;
}
.stat-card:nth-child(3) {
  animation-delay: 0.3s;
}
.stat-card:nth-child(4) {
  animation-delay: 0.4s;
}
</style>
